import type { ChecklistDraftItem } from '../model/taskDraft';
import { MAX_CHECKLIST_TEXT_LENGTH } from '../../../shared/lib/constants';
import { createUuid } from '../../../shared/lib/uuid';

export interface ChecklistEditorProps {
  items: ChecklistDraftItem[];
  onChange: (items: ChecklistDraftItem[]) => void;
}

/** Редактор чек-листа: пункты, done, порядок, удаление. */
export function ChecklistEditor({ items, onChange }: ChecklistEditorProps) {
  function updateItem(id: string, patch: Partial<ChecklistDraftItem>): void {
    onChange(items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  function move(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= items.length) {
      return;
    }
    const next = [...items];
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) {
      return;
    }
    next[index] = b;
    next[target] = a;
    onChange(
      next.map((item, idx) => ({
        ...item,
        sortOrder: idx,
      })),
    );
  }

  function remove(id: string): void {
    onChange(items.filter((item) => item.id !== id));
  }

  function add(text: string): void {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      return;
    }
    onChange([
      ...items,
      {
        id: `new-${createUuid()}`,
        text: trimmed,
        done: false,
        sortOrder: items.length,
        isNew: true,
      },
    ]);
  }

  return (
    <div className="space-y-2">
      <p className="tg-label mb-0">Чек-лист</p>
      {items.length > 0 ? (
        <ul className="space-y-1.5">
          {items.map((item, index) => (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded-xl border border-tg-border-soft bg-tg-bg px-2 py-1.5"
            >
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-tg-accent"
                checked={item.done}
                aria-label={`Пункт: ${item.text}`}
                onChange={(event) => {
                  updateItem(item.id, { done: event.target.checked });
                }}
              />
              <input
                className="min-w-0 flex-1 bg-transparent text-sm text-tg-text outline-none"
                value={item.text}
                maxLength={MAX_CHECKLIST_TEXT_LENGTH}
                aria-label="Текст пункта чек-листа"
                onChange={(event) => {
                  updateItem(item.id, { text: event.target.value });
                }}
              />
              <div className="flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  className="rounded p-1 text-tg-text-muted hover:text-tg-text"
                  aria-label="Переместить выше"
                  disabled={index === 0}
                  onClick={() => {
                    move(index, -1);
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="rounded p-1 text-tg-text-muted hover:text-tg-text"
                  aria-label="Переместить ниже"
                  disabled={index === items.length - 1}
                  onClick={() => {
                    move(index, 1);
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="rounded p-1 text-tg-text-muted hover:text-tg-danger"
                  aria-label="Удалить пункт"
                  onClick={() => {
                    remove(item.id);
                  }}
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      <ChecklistAddForm onAdd={add} />
    </div>
  );
}

function ChecklistAddForm({ onAdd }: { onAdd: (text: string) => void }) {
  return (
    <form
      className="flex gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const input = form.elements.namedItem('checklist-text');
        if (!(input instanceof HTMLInputElement)) {
          return;
        }
        onAdd(input.value);
        input.value = '';
      }}
    >
      <input
        name="checklist-text"
        className="tg-input h-9 min-h-9 flex-1"
        placeholder="Новый пункт"
        maxLength={MAX_CHECKLIST_TEXT_LENGTH}
        aria-label="Новый пункт чек-листа"
      />
      <button type="submit" className="tg-btn-secondary h-9 min-h-9 px-3 text-sm">
        Добавить
      </button>
    </form>
  );
}
